import type { AlertDetail, LatLng, TelemetryPoint } from "./fuelReportTypes"

export type LngLatBounds = [[number, number], [number, number]]

/**
 * Collect every known point for a fuel event (suggested stations, driver
 * position at send time, refuel point, telemetry track) and compute a
 * maplibre-style bounds box `[[minLng, minLat], [maxLng, maxLat]]` so the
 * detail map can fit all markers on open. Returns `null` when there are no
 * points to bound (caller should fall back to the map's default view).
 */
export function computeEventBounds(
  detail: AlertDetail | null | undefined,
  telemetry: TelemetryPoint[],
): LngLatBounds | null {
  const points: LatLng[] = []

  if (detail) {
    points.push(...detail.suggested)
    if (detail.driverPos) points.push(detail.driverPos)
    if (
      detail.refuel &&
      detail.refuel.lat != null &&
      detail.refuel.lng != null
    ) {
      points.push({ lat: detail.refuel.lat, lng: detail.refuel.lng })
    }
    if (
      detail.seen?.seen &&
      detail.seen.seenLat != null &&
      detail.seen.seenLng != null
    ) {
      points.push({ lat: detail.seen.seenLat, lng: detail.seen.seenLng })
    }
  }

  for (const t of telemetry) points.push({ lat: t.lat, lng: t.lng })

  if (points.length === 0) return null

  let minLat = points[0].lat
  let maxLat = points[0].lat
  let minLng = points[0].lng
  let maxLng = points[0].lng
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat
    if (p.lat > maxLat) maxLat = p.lat
    if (p.lng < minLng) minLng = p.lng
    if (p.lng > maxLng) maxLng = p.lng
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}
